/**
 * @fileoverview Component for confirming the assignment of a new test.
 * This component includes a confirmation message and a button for confirming the assignment.
 */

import { XMarkIcon } from '@heroicons/react/24/solid'
import { useModal } from '../../../hooks/useModal'
import { PrimaryButton } from '../../elements/PrimaryButton'
import { RegularButton } from '../../elements/RegularButton'

/**
 * @interface ConfirmNewTestProps
 * @description Props for the ConfirmNewTest component.
 * @property {function} onConfirm - The function to call when confirmation is confirmed. Returns a Promise.
 */
interface ConfirmNewTestProps {
    onConfirm: () => Promise<void>
}

/**
 * @function ConfirmNewTest
 * @description Component for confirming the assignment of a new test.
 * @param {ConfirmNewTestProps} props - The props for the component.
 * @returns {JSX.Element} - A React component that renders the confirmation message and button.
 */
export function ConfirmNewTest({ onConfirm }: ConfirmNewTestProps) {
    const [, close] = useModal()
    return (
        <div className="w-[26rem]">
            <div className="flex justify-between gap-4 mb-6">
                <div className="flex">
                    <h2 className="block font-semibold text-xl text-gray-900">
                        Assignment of a new test will cancel the currently assigned test.
                    </h2>
                </div>
                <button className="flex shrink-0 w-6 h-6" onClick={close}>
                    <XMarkIcon className="w-6 h-6" />
                </button>
            </div>
            <div>
                <p className="text-sm text-gray-600">
                    When a new test is assigned, any existing test, not started or not completed,
                    will be cancelled, and the participant will lose access to it.
                </p>
            </div>

            <section className="flex flex-row gap-2 justify-end mt-8">
                <RegularButton onClick={close}>Cancel</RegularButton>
                <PrimaryButton
                    onClick={async () => {
                        await onConfirm()
                        close()
                    }}
                >
                    Assign New Test Anyway
                </PrimaryButton>
            </section>
        </div>
    )
}
